import React, { useState, useEffect, useContext, useRef } from 'react';
import { styled, alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import EditIcon from '@mui/icons-material/Edit';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import DeleteIcon from '@mui/icons-material/Delete';

import { OperatorContext } from '../../contexts/OperatorContext';
import ManageOperatorForm from '../CMSForm/ManageOperatorForm';
import ManageUsersForm from '../CMSForm/ManageUsersForm'
import OperatorActionErrorModal from '../CMSForm/OperatorActionErrorModal';

import './ManageOperators.css'


const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.black, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.black, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));

function ManageOperators() {
  const { getOperators, operators, getOperatorCount, operatorCount, searchOperator, operatorsSearch, publishOperator, deleteOperator, page, setPage } = useContext(OperatorContext)
  const [hidden, setHidden] = useState(false)
  const [passwordModalHidden, setPasswordModalHidden] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [errorModal, setErrorModal] = useState(null)
  const [selectedOperatorForDelete, setSelectedOperatorForDelete] = useState(null)
  const [searchString, setSearchString] = useState('')
  const [filteredData, setFilteredData] = useState([])
  const [editFormData, setEditFormData] = useState({})
  const [passwordOperatorSelected, setPasswordOperatorSelected] = useState('')
  const [passwordOperatorType, setPasswordOperatorType] = useState('')
  const [actionError, setActionError] = useState('')
  const publishInFlight = useRef(new Set())
  const [, setPublishInFlight] = useState(0)
  const deleteInFlight = useRef(new Set())

  function toggleForm() {
    setHidden(!hidden)
  }

  useEffect(() => {
    if (searchString !== '') {
      searchOperator(searchString).catch(() => {})
    } else {
      getOperators(page).catch(() => {})
    }
  }, [page])

  useEffect(() => {
    if (operators.length || operatorCount) {
      getOperatorCount().catch(() => {})
    }
    if (searchString !== '') {
      return
    }
    setFilteredData([...processData(operators)])
  }, [operators])


  useEffect(() => {
    if (searchString !== '') {
      setFilteredData([...processData(operatorsSearch)])
    }
  }, [operatorsSearch])

  function processData(data) {
    const newData = data.map(e => createData(e._id, e.name, `${e.address}, ${e.city} ${e.state}`, e.phone, e.active, e.organization_type, e.product_access))
    return newData
  }

  function createData(_id, name, address, phone, active, organization_type, product_access) {
    return { _id, name, address, phone, active, organization_type, product_access };
  }

  const ORGANIZATION_TYPE_LABELS = {
    operator: 'Operator',
    consultant: 'Consultant',
    hospital: 'Hospital'
  }

  const PRODUCT_ACCESS_LABELS = {
    vao: 'VAO',
    clearcaid: 'ClearCaid'
  }

  function formatOrganizationType(type) {
    if (!type) return '—'
    return ORGANIZATION_TYPE_LABELS[type] || type
  }

  function formatProductAccess(access) {
    if (!Array.isArray(access) || access.length === 0) return '—'
    return access.map(a => PRODUCT_ACCESS_LABELS[a] || a).join(', ')
  }

  function handleSearch(e) {
    let string = e.target.value
    setSearchString(string)
    setPage(1)

    if (string !== '') {
      searchOperator(string).catch(() => {})
    }
    else {
      getOperators(1).catch(() => {})
    }
  }

  function handleCreateNew() {
    setEditFormData({})
    toggleForm()
  }

  function handleEditClick(id) {
    let selected = getCurrListData(id)
    if (!selected) return
    setEditFormData({ ...selected })
    toggleForm()
  }

  async function handlePublish(id) {
    let selected = getCurrListData(id)
    if (!selected?._id) return
    const inFlightId = String(selected._id)
    if (publishInFlight.current.has(inFlightId)) return
    publishInFlight.current.add(inFlightId)
    setPublishInFlight((n) => n + 1)
    setActionError('')
    try {
      await publishOperator(selected._id, !selected.active, searchString)
    } catch (error) {
      console.error('Error submitting form:', error)
      setActionError('Failed to update organization publishing status.')
    } finally {
      publishInFlight.current.delete(inFlightId)
      setPublishInFlight((n) => n + 1)
    }
  }

  function handlePasswordClick(id) {
    let selected = getCurrListData(id)
    if (!selected) return
    setPasswordModalHidden(true)
    setPasswordOperatorSelected(selected._id)
    setPasswordOperatorType(selected.organization_type)
  }

  function getCurrListData(id) {
    return (searchString !== '' ? operatorsSearch : operators)
      .find((operator) => String(operator._id) === String(id))
  }

  async function handleDelete(additionalFlags = {}) {
    let selected = (searchString !== '' ? operatorsSearch : operators).find((o) => String(o._id) === String(deleteConfirm || selectedOperatorForDelete?._id))
    if (!selected?._id) {
      setDeleteConfirm(null)
      setErrorModal(null)
      setSelectedOperatorForDelete(null)
      return
    }
    const inFlightId = String(selected._id)
    if (deleteInFlight.current.has(inFlightId)) {
      setDeleteConfirm(null)
      return
    }
    deleteInFlight.current.add(inFlightId)
    setActionError('')
    try {
      // Store the selection for potential retries
      if (!selectedOperatorForDelete) {
        setSelectedOperatorForDelete(selected)
      }
      setDeleteConfirm(null)
      await deleteOperator(selected._id, additionalFlags, searchString)
      setErrorModal(null)
      setSelectedOperatorForDelete(null)
    } catch (error) {
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.error === 'organization_has_active_subscriptions' ||
            errorData.error === 'operator_has_active_subscriptions' ||
            errorData.error === 'require_move_and_delete_organization' ||
            errorData.error === 'organization_has_assigned_users' ||
            errorData.error === 'operator_has_assigned_users') {
          setDeleteConfirm(null); // Close the confirmation dialog
          setErrorModal(errorData);
          return;
        }
      }
      setDeleteConfirm(null);
      setSelectedOperatorForDelete(null);
      console.error('Error submitting form:', error);
      setActionError('Failed to delete organization.')
    } finally {
      deleteInFlight.current.delete(inFlightId)
    }
  }

  const handleMoveAndDeleteOperator = () => {
    handleDelete({ move_and_delete_organization: true });
  };

  const handleRetry = () => {
    handleDelete();
  };

  const handleRetryWithCancelSubscription = () => {
    handleDelete({ cancel_active_subscription: true, move_and_delete_organization: true });
  };

  const handleCancelErrorModal = () => {
    setErrorModal(null);
    setSelectedOperatorForDelete(null);
  };

  return (
    <div>
      <OperatorActionErrorModal
        error={errorModal}
        onRetry={handleRetry}
        onCancel={handleCancelErrorModal}
        onRetryWithCancelSubscription={handleRetryWithCancelSubscription}
        onMoveAndDeleteOperator={handleMoveAndDeleteOperator}
      />
      <Box sx={{ flexGrow: 1 }}>
        <AppBar style={{ backgroundColor: 'white' }} position="static">
          <Toolbar style={{ display: 'flex', justifyContent: 'space-between' }} >
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search…"
                inputProps={{ 'aria-label': 'search' }}
                onChange={(e) => { handleSearch(e) }}
                value={searchString}
              />
            </Search>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Button onClick={handleCreateNew} variant="contained">Create New</Button>
            </div>
          </Toolbar>
        </AppBar>
      </Box>

      {/* form */}
      {hidden && <ManageOperatorForm
        initialData={editFormData}
        closeForm={() => { toggleForm() }}
        searchString={searchString}
      />}

      <TableContainer style={{ marginTop: '30px' }} component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Product Access</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((row, i) => {
              const rowInFlight = publishInFlight.current.has(String(row._id)) || deleteInFlight.current.has(String(row._id))
              return (
              <TableRow
                key={String(row._id)}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.address}</TableCell>
                <TableCell>{row.phone}</TableCell>
                <TableCell>{formatOrganizationType(row.organization_type)}</TableCell>
                <TableCell>{formatProductAccess(row.product_access)}</TableCell>
                <TableCell>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>

                    <TableOption
                      color={'inherit'}
                      name={filteredData[i].active ? 'Unpublish' : 'Publish'}
                      onClick={() => { handlePublish(row._id) }}
                      disabled={rowInFlight}
                    >
                      {filteredData[i].active ?
                        <RemoveRedEyeIcon />
                        :
                        <VisibilityOffIcon />
                      }
                    </TableOption>

                    <TableOption
                      color={'primary'}
                      name={'Edit'}
                      onClick={() => { handleEditClick(row._id) }}
                      disabled={rowInFlight}
                    >
                      <EditIcon />
                    </TableOption>

                    <TableOption
                      color={'primary'}
                      name={'Manage Users'}
                      onClick={() => { handlePasswordClick(row._id) }}
                      disabled={rowInFlight}
                    >
                      <LockIcon />
                    </TableOption>

                    <TableOption
                      color={'warning'}
                      name={'Delete'}
                      disabled={rowInFlight}
                      onClick={() => {
                        const selected = getCurrListData(row._id)
                        if (!selected?._id) return
                        if (deleteInFlight.current.has(String(selected._id))) return
                        setDeleteConfirm(selected._id)
                      }}
                    >
                      <DeleteIcon />
                    </TableOption>
                  </div>
                </TableCell>
              </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {actionError && <p style={{ textAlign: 'center', color: '#E23844' }}>{actionError}</p>}

      {searchString === '' && operatorCount > 15 &&
        <div style={{ width: '100%', margin: '40px 0px', display: 'flex', justifyContent: 'center' }}>
          <Stack spacing={2}>
            <Pagination page={page} onChange={(e, i) => { setPage(i) }} count={Math.ceil(operatorCount / 15)} showFirstButton={operatorCount / 15 > 5 ? true : false} showLastButton={operatorCount / 15 > 5 ? true : false} />
          </Stack>
        </div>
      }

      {passwordModalHidden &&
        <ManageUsersForm
          modalOpen={passwordModalHidden}
          closeForm={(e) => { setPasswordModalHidden(false) }}
          organization_id={passwordOperatorSelected}
          organization_type={passwordOperatorType}
        />
      }

      <Dialog
        open={deleteConfirm !== null}
        onClose={() => { setDeleteConfirm(null) }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {`delete operator`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {`Are you sure you want to delete ${(searchString !== '' ? operatorsSearch : operators).find((o) => String(o._id) === String(deleteConfirm))?.name || ''}?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteConfirm(null) }}>Cancel</Button>
          <Button onClick={() => { handleDelete() }} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default ManageOperators

const TableOption = ({ children, name, color, onClick, disabled = false }) => {
  return (
    <div style={{ margin: '0px 10px', cursor: disabled ? 'not-allowed' : 'pointer' }} onClick={disabled ? undefined : onClick}>
      <Tooltip title={name}>
        <IconButton color={color} disabled={disabled}>
          {children}
        </IconButton>
      </Tooltip>
    </div>
  )
}